import React, { useState, useEffect } from 'react';
import api from '../../services/api';

export default function Reports() {
  const [tab, setTab] = useState('completion');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]);
  const [to, setTo]     = useState(new Date().toISOString().split('T')[0]);

  const load = () => {
    setLoading(true);
    const endpoints = {
      completion: '/reports/completion',
      attendance: `/reports/attendance?from=${from}&to=${to}`,
      psr: '/reports/psr',
    };
    api.get(endpoints[tab]).then(r => setData(r.data.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [tab, from, to]);

  const exportCSV = () => {
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const rows = data.map(r => headers.map(h => `"${r[h] ?? ''}"`).join(','));
    const csv  = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url; a.download = `${tab}_report.csv`; a.click();
  };

  const statusColors = { not_started: 'secondary', in_progress: 'primary', completed: 'success' };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div className="d-flex gap-2">
          {[
            { key: 'completion', label: 'Course Completion' },
            { key: 'attendance', label: 'Attendance' },
            { key: 'psr',        label: 'PSR' },
          ].map(t => (
            <button key={t.key} className={`btn btn-sm ${tab===t.key ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="d-flex gap-2 align-items-center">
          {tab === 'attendance' && (
            <>
              <input type="date" className="form-control form-control-sm" value={from} onChange={e=>setFrom(e.target.value)} />
              <span className="text-muted small">to</span>
              <input type="date" className="form-control form-control-sm" value={to} onChange={e=>setTo(e.target.value)} />
            </>
          )}
          <button className="btn btn-outline-success btn-sm" onClick={exportCSV} disabled={!data.length}>
            <i className="bi bi-download me-1" />Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
      ) : (
        <>
          {tab === 'completion' && (
            <div className="table-card">
              <table className="table table-hover align-middle mb-0">
                <thead><tr><th>Employee</th><th>Dept</th><th>Course</th><th>Progress</th><th>Status</th><th>Last Active</th></tr></thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr><td colSpan={6} className="text-center text-muted py-3">No data</td></tr>
                  ) : data.map((r, i) => (
                    <tr key={i}>
                      <td>
                        <div className="small fw-semibold">{r.employee_name}</div>
                        <div className="text-muted" style={{fontSize:'.72rem'}}>{r.employee_id}</div>
                      </td>
                      <td className="small text-muted">{r.department || '—'}</td>
                      <td className="small">{r.course_title}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="progress flex-grow-1" style={{height:6,minWidth:60}}>
                            <div className={`progress-bar ${r.progress_pct>=100?'bg-success':r.progress_pct>0?'bg-primary':'bg-secondary'}`} style={{width:`${r.progress_pct||0}%`}} />
                          </div>
                          <span className="small">{r.progress_pct||0}%</span>
                        </div>
                      </td>
                      <td><span className={`badge bg-${statusColors[r.status]||'secondary'}`}>{r.status?.replace('_',' ')||'—'}</span></td>
                      <td className="small text-muted">{r.last_active ? new Date(r.last_active).toLocaleDateString('en-IN') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'attendance' && (
            <div className="table-card">
              <table className="table table-hover align-middle mb-0">
                <thead><tr><th>Employee</th><th>Dept</th><th>Total</th><th>Present</th><th>Absent</th><th>Leave</th><th>%</th></tr></thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr><td colSpan={7} className="text-center text-muted py-3">No data</td></tr>
                  ) : data.map((r, i) => (
                    <tr key={i}>
                      <td>
                        <div className="small fw-semibold">{r.name}</div>
                        <div className="text-muted" style={{fontSize:'.72rem'}}>{r.employee_id}</div>
                      </td>
                      <td className="small text-muted">{r.department || '—'}</td>
                      <td className="small">{r.total_days}</td>
                      <td><span className="badge bg-success">{r.present}</span></td>
                      <td><span className="badge bg-danger">{r.absent}</span></td>
                      <td><span className="badge bg-warning text-dark">{r.leaves}</span></td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="progress" style={{height:6,width:60}}>
                            <div className={`progress-bar ${r.attendance_pct>=80?'bg-success':r.attendance_pct>=60?'bg-warning':'bg-danger'}`} style={{width:`${r.attendance_pct}%`}} />
                          </div>
                          <span className="small fw-semibold">{r.attendance_pct}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'psr' && (
            <div className="table-card">
              <table className="table table-hover align-middle mb-0">
                <thead><tr><th>Employee</th><th>Dept</th><th>Entries</th><th>Avg Score</th><th>Max</th><th>Min</th><th>Last Entry</th></tr></thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr><td colSpan={7} className="text-center text-muted py-3">No data</td></tr>
                  ) : data.map((r, i) => (
                    <tr key={i}>
                      <td>
                        <div className="small fw-semibold">{r.name}</div>
                        <div className="text-muted" style={{fontSize:'.72rem'}}>{r.employee_id}</div>
                      </td>
                      <td className="small text-muted">{r.department || '—'}</td>
                      <td className="small">{r.total_entries}</td>
                      <td>
                        <span className={`fw-bold text-${+r.avg_score>=80?'success':+r.avg_score>=60?'warning':'danger'}`}>{r.avg_score}%</span>
                      </td>
                      <td className="small text-success">{r.max_score}%</td>
                      <td className="small text-danger">{r.min_score}%</td>
                      <td className="small text-muted">{r.last_entry ? new Date(r.last_entry).toLocaleDateString('en-IN') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}
